"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import posthogClient from "posthog-js";
import { useEffect, useState } from "react";
import { type ConsentStatus, useConsent } from "@/lib/stores/consent-store";
import { Button } from "@domainstack/ui/button";

/**
 * Minimal cookie consent banner for GDPR compliance.
 *
 * - Only shows for EU/EEA users (based on geolocation header from request)
 * - Non-EU users are auto-accepted silently
 * - Syncs with PostHog opt-in/opt-out
 */
export function CookiePrompt(t0) {
  const $ = _c(27);
  const {
    consentRequired
  } = t0;
  const {
    consent,
    setConsent,
    persistent: isPersistent
  } = useConsent();
  const [isExiting, setIsExiting] = useState(false);
  let t1;
  let t2;
  if ($[0] !== consent || $[1] !== consentRequired || $[2] !== isPersistent || $[3] !== setConsent) {
    t1 = () => {
      if (isPersistent && consent === "pending" && !consentRequired) {
        setConsent("accepted");
      }
    };
    t2 = [isPersistent, consent, consentRequired, setConsent];
    $[0] = consent;
    $[1] = consentRequired;
    $[2] = isPersistent;
    $[3] = setConsent;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== consent || $[7] !== isPersistent) {
    t3 = () => {
      if (!isPersistent) {
        return;
      }
      if (consent === "accepted") {
        posthogClient.opt_in_capturing();
      } else {
        if (consent === "declined") {
          posthogClient.opt_out_capturing();
        }
      }
    };
    t4 = [consent, isPersistent];
    $[6] = consent;
    $[7] = isPersistent;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== setConsent) {
    t5 = consentStatus => {
      setIsExiting(true);
      setTimeout(() => {
        setConsent(consentStatus);
        setIsExiting(false);
      }, 200);
    };
    $[10] = setConsent;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleHide = t5;
  let t6;
  if ($[12] !== handleHide) {
    t6 = () => {
      posthogClient.opt_in_capturing();
      handleHide("accepted");
    };
    $[12] = handleHide;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const accept = t6;
  let t7;
  if ($[14] !== handleHide) {
    t7 = () => {
      posthogClient.opt_out_capturing();
      handleHide("declined");
    };
    $[14] = handleHide;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const decline = t7;
  const show = isPersistent && consent === "pending" && consentRequired;
  if (!show) {
    return null;
  }
  const t8 = `fixed bottom-3 left-3 z-100 max-w-[260px] duration-200 motion-reduce:animate-none motion-reduce:transition-none ${isExiting ? "animate-out slide-out-to-bottom-8" : "animate-in slide-in-from-bottom-8"}`;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-xs leading-relaxed text-pretty text-muted-foreground">We use cookies to understand how you use our service.{" "}<Link href="/privacy#cookies" className="text-foreground/80 underline underline-offset-2 hover:text-foreground">Learn more.</Link></p>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== decline) {
    t10 = <Button variant="ghost" size="sm" onClick={decline} className="h-7 px-2.5 text-xs text-muted-foreground">Decline</Button>;
    $[17] = decline;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== accept) {
    t11 = <Button size="sm" onClick={accept} className="h-7 bg-foreground px-2.5 text-xs text-background hover:bg-foreground/90">Accept</Button>;
    $[19] = accept;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <div role="dialog" aria-label="Cookie consent" className="rounded-lg border bg-card p-3 shadow-md">{t9}<div className="mt-2.5 flex items-center gap-1.5">{t10}{t11}</div></div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t8) {
    t13 = <div className={t8}>{t12}</div>;
    $[24] = t12;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}